import {ConditionType, ItemType, PromotionType, RewardType} from '@/types/promotion'
import {z} from 'zod'

const ItemSchema = z.object({
  id: z.string(),
  name: z.string(),
})

const FreeProductRewardSchema = z.object({
  type: z.literal(RewardType.FREE_PRODUCT),
  quantity: z.number().int().min(1),
  items: z.array(ItemSchema).nonempty(),
})

const PointsRewardSchema = z.object({
  type: z.literal(RewardType.POINTS),
  points: z.number().int().min(1),
})

const DiscountRewardSchema = z.object({
  type: z.literal(RewardType.DISCOUNT),
  discountType: z.string(),
  discountValue: z.number().min(1),
})

const GiftItemRewardSchema = z.object({
  type: z.literal(RewardType.GIFT_ITEM),
  quantity: z.number().int().min(1),
  items: z.array(z.string()).nonempty(),
})

const RewardSchema = z.discriminatedUnion('type', [
  FreeProductRewardSchema,
  PointsRewardSchema,
  DiscountRewardSchema,
  GiftItemRewardSchema,
])

const SelectedItemSchema = z.object({
  id: z.string(),
  displayText: z.string(),
})

const ConditionSchema = z.object({
  type: z.literal(ConditionType.BUY),
  quantity: z.number().int().nullable().optional(),
  itemType: z.nativeEnum(ItemType),
  items: z.array(ItemSchema).nonempty(),
  selectedItems: z.array(SelectedItemSchema).optional(),
  productCombination: z.boolean().nullable().optional(),
})

const ConditionGroupSchema = z.object({
  conditions: z.array(ConditionSchema),
})

const BasePromotionConditionSchema = z.object({
  sequence: z.number().int(),
  conditionGroup: ConditionGroupSchema.optional(),
  rewards: z.array(RewardSchema).nonempty(), // Made mandatory for all types
})

export function getValidPromotionConditions(promotionConditions, promotionType) {
  const validConditions = []

  promotionConditions?.forEach(condition => {
    try {
      const baseCondition = condition

      // Rewards are now mandatory for all promotion types (validated in BasePromotionConditionSchema)
      if (!baseCondition.rewards || baseCondition.rewards.length === 0) {
        throw new Error('rewards are required for all promotion types')
      }

      if (promotionType === PromotionType.BUY_SINGLE_PRODUCT) {
        if (!baseCondition.conditionGroup) {
          throw new Error('conditionGroup is required for single product promotions')
        }

        if (!baseCondition.conditionGroup.conditions || baseCondition.conditionGroup.conditions.length === 0) {
          throw new Error('conditionGroup must have at least one condition')
        }
      } else if (promotionType === PromotionType.BUY_MULTIPLE_PRODUCTS) {
        if (!baseCondition.conditionGroup) {
          throw new Error('conditionGroup is required for multiple product promotions')
        }

        if (!baseCondition.conditionGroup.conditions || baseCondition.conditionGroup.conditions.length < 2) {
          throw new Error('conditionGroup must have at least two conditions for multiple products')
        }
      } else if (promotionType === PromotionType.STORE_DISCOUNT || promotionType === PromotionType.WELCOME_DISCOUNT) {
        // Additional validation for discount promotions - ensure they have a discount reward
        const hasDiscountReward = baseCondition.rewards.some(reward => reward.type === RewardType.DISCOUNT)

        if (!hasDiscountReward) {
          throw new Error('discount promotions must include at least one discount reward')
        }
      }

      validConditions.push(baseCondition)
    } catch {
      // Validation errors are expected while the user is filling the form
    }
  })

  return {
    hasValid: validConditions.length > 0,
    validConditions,
    validCount: validConditions.length,
    totalCount: promotionConditions.length,
  }
}

export const SingleProductPromotionConditionSchema = BasePromotionConditionSchema.extend({
  conditionGroup: ConditionGroupSchema,
  rewards: z.array(RewardSchema).nonempty(), // Explicitly mandatory
})

export const MultipleProductPromotionConditionSchema = BasePromotionConditionSchema.extend({
  conditionGroup: ConditionGroupSchema.refine(conditionGroup => conditionGroup.conditions.length >= 2, {
    message: 'Multiple product promotions must have at least two conditions',
  }),
  rewards: z.array(RewardSchema).nonempty(), // Explicitly mandatory
})

export const DiscountPromotionConditionSchema = BasePromotionConditionSchema.extend({
  conditionGroup: ConditionGroupSchema.optional(),
  rewards: z.array(RewardSchema).nonempty(), // Already mandatory, keeping explicit
})

export function getPromotionConditionSchema(promotionType) {
  switch (promotionType) {
    case PromotionType.BUY_SINGLE_PRODUCT:
      return SingleProductPromotionConditionSchema
    case PromotionType.BUY_MULTIPLE_PRODUCTS:
      return MultipleProductPromotionConditionSchema
    case PromotionType.STORE_DISCOUNT:
    case PromotionType.WELCOME_DISCOUNT:
      return DiscountPromotionConditionSchema
    default:
      return BasePromotionConditionSchema
  }
}

export function validatePromotionConditionsWithSchema(promotionConditions, promotionType) {
  const schema = getPromotionConditionSchema(promotionType)
  const validConditions = []

  promotionConditions.forEach(condition => {
    try {
      const validCondition = schema.parse(condition)
      validConditions.push(validCondition)
    } catch (error) {
      // Error handling could be added here if needed
    }
  })

  return {
    hasValid: validConditions.length > 0,
    validConditions,
    validCount: validConditions.length,
    totalCount: promotionConditions.length,
  }
}
